import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Wind, X, Edit2, Download, RefreshCw, ArrowRight, CheckCircle2, Clock, Filter, Trash2, Check, Code } from 'lucide-react';
import { createPortal } from 'react-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';
// --- Types ---
interface SavedItem {
  id: string;
  text: string;
  type: 'insight' | 'trade';
  source: string;
  timestamp: string;
  tags?: string[];
}
interface WeatherRegime {
  id: string;
  region: string;
  pattern: string;
  timeHorizon: string;
  confidence: 'high' | 'medium' | 'low';
  description: string;
  detectionLogic: string[];
  marketImpact: 'high' | 'medium' | 'low';
  lastUpdated: string;
  persistence: number;
  modelAgreement: number;
}
interface StrategyArchetype {
  id: string;
  name: string;
  instruments: string[];
  holdingPeriod: string;
  riskProfile: 'low' | 'medium' | 'high';
  fitScore: 'high' | 'medium' | 'low';
  worksWhen: string;
  failsWhen: string;
  description: string;
  keyWeatherDriver: string;
  invalidationTriggers: string[];
}
interface SimulatedTrade {
  id: string;
  strategyName: string;
  region: string;
  instrument: string;
  positionType: 'long' | 'short' | 'hedge';
  notional: number;
  entryTiming: string;
  weatherDependency: string;
  timeHorizon: string;
  pnlRange: {
    p10: number;
    p50: number;
    p90: number;
  };
  alertsEnabled: boolean;
  status: 'draft' | 'active' | 'monitoring' | 'closed';
  lastUpdate: string;
}
// --- Mock Data ---
const mockRegimes: WeatherRegime[] = [{
  id: 'regime-1',
  region: 'Germany',
  pattern: 'Sustained low wind risk',
  timeHorizon: '3-7 days',
  confidence: 'high',
  marketImpact: 'high',
  description: 'Extended period of below-average wind generation expected across Northern Germany, with high-pressure systems limiting renewable output.',
  detectionLogic: ['Wind capacity weighted 25% below seasonal baseline', 'Pattern persistent across 4+ forecast runs', 'Model agreement: 85%'],
  lastUpdated: '5 min ago',
  persistence: 4,
  modelAgreement: 85
}, {
  id: 'regime-2',
  region: 'Spain',
  pattern: 'High solar output period',
  timeHorizon: '2-5 days',
  confidence: 'medium',
  marketImpact: 'medium',
  description: 'Clear weather conditions driving above-average solar generation across the Iberian Peninsula.',
  detectionLogic: ['Solar irradiance 18% above seasonal average', 'Low cloud cover forecast across Iberia', 'Model agreement: 72%'],
  lastUpdated: '12 min ago',
  persistence: 3,
  modelAgreement: 72
}, {
  id: 'regime-3',
  region: 'Nordics',
  pattern: 'Extreme cold snap',
  timeHorizon: '5-10 days',
  confidence: 'high',
  marketImpact: 'high',
  description: 'Significant temperature drop increasing heating demand across Scandinavia.',
  detectionLogic: ['Temperature anomaly: -8°C vs baseline', 'Persistent across ensemble members', 'Model agreement: 91%'],
  lastUpdated: '2 min ago',
  persistence: 5,
  modelAgreement: 91
}];
const strategyLibrary: Record<string, StrategyArchetype[]> = {
  'regime-1': [{
    id: 'strat-1-1',
    name: 'Long Baseload Power',
    instruments: ['DE Power Futures', 'Day-ahead'],
    holdingPeriod: '3-7 days',
    riskProfile: 'medium',
    fitScore: 'high',
    worksWhen: 'Low renewable output, high demand',
    failsWhen: 'Unexpected renewable surge',
    description: 'Position benefits from tight supply conditions when wind generation underperforms',
    keyWeatherDriver: 'Wind speed < 3m/s',
    invalidationTriggers: ['Wind forecast upgrade > 2GW', 'Temp increase > 3°C']
  }, {
    id: 'strat-1-2',
    name: 'Short Wind Certificates',
    instruments: ['Green certificates', 'Wind PPAs'],
    holdingPeriod: '3-7 days',
    riskProfile: 'high',
    fitScore: 'high',
    worksWhen: 'Sustained low wind generation',
    failsWhen: 'Wind picks up unexpectedly',
    description: 'Profits from reduced renewable generation and certificate scarcity',
    keyWeatherDriver: 'Wind capacity < 20% of installed',
    invalidationTriggers: ['Wind forecast revision upward', 'Storm system approaching']
  }, {
    id: 'strat-1-3',
    name: 'Gas Spread Arbitrage',
    instruments: ['DE-NL Gas Spreads'],
    holdingPeriod: '3-7 days',
    riskProfile: 'low',
    fitScore: 'medium',
    worksWhen: 'Low wind increases gas demand',
    failsWhen: 'Wind generation recovers',
    description: 'Exploits price differentials as gas-fired generation increases',
    keyWeatherDriver: 'Regional wind divergence',
    invalidationTriggers: ['Wind normalization', 'Gas supply disruption']
  }],
  'regime-2': [{
    id: 'strat-2-1',
    name: 'Short Day-ahead Power',
    instruments: ['ES Day-ahead', 'Solar PPAs'],
    holdingPeriod: '2-5 days',
    riskProfile: 'medium',
    fitScore: 'high',
    worksWhen: 'Excess solar generation depresses prices',
    failsWhen: 'Cloud cover increases',
    description: 'Benefits from oversupply during peak solar hours',
    keyWeatherDriver: 'Solar irradiance > 120% baseline',
    invalidationTriggers: ['Weather system approaching', 'Demand spike']
  }, {
    id: 'strat-2-2',
    name: 'Cross-Border Export Play',
    instruments: ['ES-FR Interconnector', 'Export capacity'],
    holdingPeriod: '2-5 days',
    riskProfile: 'low',
    fitScore: 'high',
    worksWhen: 'Spanish surplus, French deficit',
    failsWhen: 'Interconnector congestion',
    description: 'Arbitrages price differential via cross-border flows',
    keyWeatherDriver: 'Regional generation imbalance',
    invalidationTriggers: ['Interconnector outage', 'Price convergence']
  }],
  'regime-3': [{
    id: 'strat-3-1',
    name: 'Long Nordic Power',
    instruments: ['Nordic system price', 'NO/SE futures'],
    holdingPeriod: '5-10 days',
    riskProfile: 'medium',
    fitScore: 'high',
    worksWhen: 'Extreme cold increases demand',
    failsWhen: 'Temperature moderates',
    description: 'Captures demand surge from heating load increase',
    keyWeatherDriver: 'Temperature < -15°C',
    invalidationTriggers: ['Warm front approaching', 'Demand response activation']
  }, {
    id: 'strat-3-2',
    name: 'Hydro Reservoir Value',
    instruments: ['Hydro capacity rights', 'Water value contracts'],
    holdingPeriod: '5-10 days',
    riskProfile: 'low',
    fitScore: 'high',
    worksWhen: 'High prices, low reservoir levels',
    failsWhen: 'Reservoir depletion risk',
    description: 'Optimizes hydro dispatch during high-price cold period',
    keyWeatherDriver: 'Price > €100/MWh, reservoir > 50%',
    invalidationTriggers: ['Reservoir below 40%', 'Price normalization']
  }]
};
// --- Helper Components ---
function ConfidenceBadge({
  confidence
}: {
  confidence: 'high' | 'medium' | 'low';
}) {
  const styles = {
    high: 'bg-emerald-50/50 text-emerald-700 border-emerald-200/50',
    medium: 'bg-amber-50/50 text-amber-700 border-amber-200/50',
    low: 'bg-rose-50/50 text-rose-700 border-rose-200/50'
  };
  const labels = {
    high: 'High Confidence',
    medium: 'Medium Confidence',
    low: 'Low Confidence'
  };
  return <span className={`px-2.5 py-1 rounded-md text-[10px] font-medium tracking-wide border ${styles[confidence]}`} data-id="element-3213">
      {labels[confidence]}
    </span>;
}
function ImpactBadge({
  impact
}: {
  impact: 'high' | 'medium' | 'low';
}) {
  const styles = {
    high: 'text-pink-700 bg-pink-50 border-pink-200',
    medium: 'text-amber-700 bg-amber-50 border-amber-200',
    low: 'text-slate-700 bg-slate-50 border-slate-200'
  };
  return <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${styles[impact]}`} data-id="element-3214">
      {impact.toUpperCase()} IMPACT
    </span>;
}
function RiskBadge({
  risk
}: {
  risk: 'low' | 'medium' | 'high';
}) {
  const styles = {
    low: 'bg-green-50 text-green-700 border-green-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    high: 'bg-red-50 text-red-700 border-red-200'
  };
  return <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${styles[risk]}`} data-id="element-3215">
      {risk.toUpperCase()} RISK
    </span>;
}
function FitBadge({
  fit
}: {
  fit: 'high' | 'medium' | 'low';
}) {
  const styles = {
    high: 'bg-blue-50 text-blue-700 border-blue-200',
    medium: 'bg-slate-50 text-slate-700 border-slate-200',
    low: 'bg-slate-50 text-slate-500 border-slate-200'
  };
  return <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${styles[fit]}`} data-id="element-3216">
      {fit.toUpperCase()} FIT
    </span>;
}
// --- Selection Menu Component ---
function SelectionMenu({
  position,
  onSave
}: {
  position: {
    top: number;
    left: number;
  } | null;
  onSave: (type: 'insight' | 'trade') => void;
}) {
  if (!position) return null;
  return createPortal(<motion.div initial={{
    opacity: 0,
    scale: 0.9,
    y: 10
  }} animate={{
    opacity: 1,
    scale: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.9,
    y: 10
  }} style={{
    position: 'fixed',
    top: position.top,
    left: position.left,
    zIndex: 9999
  }} className="flex flex-col bg-slate-900 text-white rounded-lg shadow-xl overflow-hidden min-w-[180px]" data-id="element-3217">
      <button onClick={() => onSave('insight')} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-800 transition-colors text-sm font-medium text-left" data-id="element-3218">
        <Lightbulb size={16} className="text-amber-400" data-id="element-3219" />
        Save as Insight
      </button>
      <div className="h-px bg-slate-800" data-id="element-3220" />
      <button onClick={() => onSave('trade')} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-800 transition-colors text-sm font-medium text-left" data-id="element-3221">
        <TrendingUp size={16} className="text-emerald-400" data-id="element-3222" />
        Save as Trade
      </button>
    </motion.div>, document.body);
}
// --- Right Panel Component ---
function InsightsPanel({
  isOpen,
  setIsOpen,
  items,
  onDelete
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
  items: SavedItem[];
  onDelete: (id: string) => void;
}) {
  const [activeTab, setActiveTab] = useState<'all' | 'insight' | 'trade'>('all');
  const filteredItems = items.filter(item => activeTab === 'all' || item.type === activeTab);
  return <motion.div initial={{
    width: 0,
    opacity: 0
  }} animate={{
    width: isOpen ? 320 : 0,
    opacity: isOpen ? 1 : 0
  }} className="flex-shrink-0 bg-white border-l border-slate-200 h-[calc(100vh-4rem)] sticky top-0 overflow-hidden flex flex-col" data-id="element-3223">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50" data-id="element-3224">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2" data-id="element-3225">
          <BookOpen size={18} className="text-slate-500" data-id="element-3226" />
          Saved Items
        </h3>
        <button onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-slate-200 rounded-md text-slate-500 transition-colors" data-id="element-3227">
          <X size={16} data-id="element-3228" />
        </button>
      </div>

      <div className="flex p-2 gap-1 border-b border-slate-100" data-id="element-3229">
        {(['all', 'insight', 'trade'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-1.5 text-xs font-medium rounded-md capitalize transition-colors ${activeTab === tab ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`} data-id="element-3230">
            {tab}s
          </button>)}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-source="Insights Panel" data-id="element-3231">
        {items.length === 0 ? <div className="text-center py-10 text-slate-400" data-id="element-3232">
            <p className="text-sm" data-id="element-3233">No saved items yet.</p>
            <p className="text-xs mt-1" data-id="element-3234">Select text to save insights.</p>
          </div> : <AnimatePresence mode="popLayout" data-id="element-3235">
            {filteredItems.map(item => <motion.div key={item.id} layout initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="group relative bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md transition-all" data-id="element-3236">
                <div className="flex items-start justify-between gap-2 mb-2" data-id="element-3237">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${item.type === 'insight' ? 'bg-amber-50 text-amber-700 border border-amber-100' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'}`} data-id="element-3238">
                    {item.type === 'insight' ? <Lightbulb size={10} data-id="element-3239" /> : <TrendingUp size={10} data-id="element-3240" />}
                    {item.type}
                  </span>
                  <button onClick={() => onDelete(item.id)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded transition-all" data-id="element-3241">
                    <Trash2 size={12} data-id="element-3242" />
                  </button>
                </div>
                <p className="text-sm text-slate-800 leading-relaxed mb-3" data-id="element-3243">
                  "{item.text}"
                </p>
                <div className="flex items-center justify-between text-[10px] text-slate-400 border-t border-slate-50 pt-2" data-id="element-3244">
                  <span className="font-medium text-slate-500 truncate max-w-[120px]" data-id="element-3245">
                    {item.source}
                  </span>
                  <span data-id="element-3246">{item.timestamp}</span>
                </div>
              </motion.div>)}
          </AnimatePresence>}
      </div>
    </motion.div>;
}
// --- Highlight Cards ---
function PriceHighlightCard() {
  const currentPrice = 85;
  const minTypical = 70;
  const maxTypical = 95;
  const minRange = 50;
  const maxRange = 120;
  const rangeWidth = maxRange - minRange;
  const typicalStartPct = (minTypical - minRange) / rangeWidth * 100;
  const typicalWidthPct = (maxTypical - minTypical) / rangeWidth * 100;
  const currentPosPct = (currentPrice - minRange) / rangeWidth * 100;
  const isTypical = currentPrice >= minTypical && currentPrice <= maxTypical;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col h-full" data-id="element-3247">
      <div className="flex items-center justify-between mb-4" data-id="element-3248">
        <h3 className="text-sm font-semibold text-slate-500 flex items-center gap-2" data-id="element-3249">
          <BarChart3 size={16} data-id="element-3250" />
          Market Price
        </h3>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700" data-id="element-3251">
          Live
        </span>
      </div>
      <div className="mb-6" data-id="element-3252">
        <div className="flex items-baseline gap-2 mb-1" data-id="element-3253">
          <span className="text-3xl font-bold text-slate-900" data-id="element-3254">
            €{currentPrice}
          </span>
          <span className="text-sm font-medium text-slate-500" data-id="element-3255">/MWh</span>
        </div>
        <p className={`text-sm font-medium ${isTypical ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-3256">
          {isTypical ? 'Within typical range' : `€${Math.abs(currentPrice - maxTypical)} higher than usual`}
        </p>
      </div>
      <div className="mt-auto" data-id="element-3257">
        <div className="relative h-2 bg-slate-100 rounded-full mb-2" data-id="element-3258">
          <div className="absolute top-0 bottom-0 bg-slate-300/50 rounded-sm" style={{
          left: `${typicalStartPct}%`,
          width: `${typicalWidthPct}%`
        }} data-id="element-3259" />
          <div className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 border-2 rounded-full shadow-sm z-10 ${isTypical ? 'bg-emerald-500 border-white' : 'bg-rose-500 border-white'}`} style={{
          left: `${currentPosPct}%`,
          transform: 'translate(-50%, -50%)'
        }} data-id="element-3260" />
        </div>
        <div className="flex justify-between text-[10px] text-slate-400 font-medium" data-id="element-3261">
          <span data-id="element-3262">€{minRange}</span>
          <span className="text-slate-500" data-id="element-3263">
            Typical: €{minTypical}-€{maxTypical}
          </span>
          <span data-id="element-3264">€{maxRange}</span>
        </div>
      </div>
    </motion.div>;
}
function TimingHighlightCard() {
  const data = [{
    day: 'Mon',
    price: 82,
    optimal: false
  }, {
    day: 'Tue',
    price: 75,
    optimal: true,
    type: 'buy'
  }, {
    day: 'Wed',
    price: 78,
    optimal: false
  }, {
    day: 'Thu',
    price: 88,
    optimal: false
  }, {
    day: 'Fri',
    price: 95,
    optimal: true,
    type: 'sell'
  }, {
    day: 'Sat',
    price: 92,
    optimal: false
  }, {
    day: 'Sun',
    price: 85,
    optimal: false
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.1
  }} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col h-full" data-id="element-3265">
      <div className="flex items-center justify-between mb-2" data-id="element-3266">
        <h3 className="text-sm font-semibold text-slate-500 flex items-center gap-2" data-id="element-3267">
          <Clock size={16} data-id="element-3268" />
          Optimal Timing
        </h3>
      </div>
      <div className="flex-1 min-h-[120px] -mx-2" data-id="element-3269">
        <ResponsiveContainer width="100%" height="100%" data-id="element-3270">
          <AreaChart data={data} margin={{
          top: 10,
          right: 10,
          left: 10,
          bottom: 0
        }} data-id="element-3271">
            <defs data-id="element-3272">
              <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1" data-id="element-3273">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} data-id="element-3274" />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} data-id="element-3275" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-3276" />
            <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{
            fontSize: 10,
            fill: '#94a3b8'
          }} dy={10} data-id="element-3277" />
            <Tooltip contentStyle={{
            borderRadius: '8px',
            border: 'none',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
          }} data-id="element-3278" />
            <Area type="monotone" dataKey="price" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorPrice)" data-id="element-3279" />
            <ReferenceDot x="Tue" y={75} r={4} fill="#10b981" stroke="#fff" strokeWidth={2} data-id="element-3280" />
            <ReferenceDot x="Fri" y={95} r={4} fill="#f43f5e" stroke="#fff" strokeWidth={2} data-id="element-3281" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-center gap-4 mt-2 text-[10px] font-medium" data-id="element-3282">
        <div className="flex items-center gap-1.5" data-id="element-3283">
          <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-3284" />
          <span className="text-slate-600" data-id="element-3285">Buy Window (Tue)</span>
        </div>
        <div className="flex items-center gap-1.5" data-id="element-3286">
          <div className="w-2 h-2 rounded-full bg-rose-500" data-id="element-3287" />
          <span className="text-slate-600" data-id="element-3288">Sell Window (Fri)</span>
        </div>
      </div>
    </motion.div>;
}
function ExecutionHighlightCard() {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.2
  }} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col h-full relative overflow-hidden" data-id="element-3289">
      <div className="absolute top-0 right-0 p-3 opacity-10" data-id="element-3290">
        <Timer size={80} data-id="element-3291" />
      </div>
      <div className="flex items-center justify-between mb-4 relative z-10" data-id="element-3292">
        <h3 className="text-sm font-semibold text-slate-500 flex items-center gap-2" data-id="element-3293">
          <Zap size={16} data-id="element-3294" />
          Best Execution
        </h3>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100" data-id="element-3295">
          Approaching
        </span>
      </div>
      <div className="flex-1 flex flex-col justify-center relative z-10" data-id="element-3296">
        <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-3297">
          Next 4-6 hours
        </div>
        <p className="text-sm text-slate-500 leading-snug" data-id="element-3298">
          Weather pattern convergence suggests optimal liquidity and spread
          tightening.
        </p>
      </div>
      <div className="mt-4 relative z-10" data-id="element-3299">
        <div className="w-full bg-slate-100 rounded-full h-1.5 mb-2 overflow-hidden" data-id="element-3300">
          <div className="bg-amber-500 h-1.5 rounded-full w-2/3 animate-pulse" data-id="element-3301" />
        </div>
        <p className="text-[10px] text-slate-400 text-right" data-id="element-3302">
          Window closes at 18:00 CET
        </p>
      </div>
    </motion.div>;
}
function SimulationHighlightCard({
  onSimulate
}: {
  onSimulate: () => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.3
  }} className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border border-slate-700 p-5 shadow-sm flex flex-col h-full text-white" data-id="element-3303">
      <div className="flex items-center justify-between mb-4" data-id="element-3304">
        <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2" data-id="element-3305">
          <Target size={16} data-id="element-3306" />
          Quick Sim
        </h3>
      </div>
      <div className="space-y-3 mb-6" data-id="element-3307">
        <div className="flex justify-between items-center" data-id="element-3308">
          <span className="text-xs text-slate-400" data-id="element-3309">Entry Target</span>
          <span className="text-sm font-mono font-medium text-emerald-400" data-id="element-3310">
            €82.50
          </span>
        </div>
        <div className="flex justify-between items-center" data-id="element-3311">
          <span className="text-xs text-slate-400" data-id="element-3312">Exit Target</span>
          <span className="text-sm font-mono font-medium text-white" data-id="element-3313">
            €94.00
          </span>
        </div>
        <div className="flex justify-between items-center border-t border-slate-700 pt-2" data-id="element-3314">
          <span className="text-xs text-slate-400" data-id="element-3315">Exp. P&L</span>
          <span className="text-sm font-mono font-bold text-emerald-400" data-id="element-3316">
            +13.9%
          </span>
        </div>
      </div>
      <button onClick={onSimulate} className="mt-auto w-full py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold uppercase tracking-wider rounded-lg transition-colors shadow-lg shadow-blue-900/20 flex items-center justify-center gap-2" data-id="element-3317">
        <Code size={14} data-id="element-3318" />
        Run Simulation
      </button>
    </motion.div>;
}
// --- Main Component ---
export function TradingStrategiesView() {
  const [selectedRegime, setSelectedRegime] = useState<WeatherRegime | null>(null);
  const [selectedStrategy, setSelectedStrategy] = useState<StrategyArchetype | null>(null);
  const [showSimulationModal, setShowSimulationModal] = useState(false);
  const [simulatedTrades, setSimulatedTrades] = useState<SimulatedTrade[]>([]);
  const [editingTradeId, setEditingTradeId] = useState<string | null>(null);
  const [editingNotional, setEditingNotional] = useState<number>(0);
  const [tradeToDelete, setTradeToDelete] = useState<SimulatedTrade | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletedTrade, setDeletedTrade] = useState<SimulatedTrade | null>(null);
  const [showUndoToast, setShowUndoToast] = useState(false);
  const [notional, setNotional] = useState(1000000);
  const [positionType, setPositionType] = useState<'long' | 'short' | 'hedge'>('long');
  const [entryTiming, setEntryTiming] = useState('now');
  const [preferredProducts, setPreferredProducts] = useState<string[]>([]);
  const [viewingExistingTrade, setViewingExistingTrade] = useState<SimulatedTrade | null>(null);
  const [isEditingNotionalInModal, setIsEditingNotionalInModal] = useState(false);
  const [originalNotional, setOriginalNotional] = useState<number>(0);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [guardrailsText, setGuardrailsText] = useState('');
  const [activeRole, setActiveRole] = useState<string | null>(null);
  const guardrailPresets = [{
    id: 'trader',
    role: 'Trader',
    icon: TrendingUp,
    theme: 'green',
    content: '• Maximum position size: 5% of portfolio\n• Stop loss: 2% below entry\n• Risk/reward ratio: minimum 1:2'
  }, {
    id: 'meteorologist',
    role: 'Meteorologist',
    icon: Wind,
    theme: 'cyan',
    content: '• Only use forecasts with 70%+ model agreement\n• Verify patterns across multiple data sources'
  }, {
    id: 'quant',
    role: 'Quants/Developers',
    icon: Code,
    theme: 'purple',
    content: '• Backtest strategies over minimum 3-year period\n• Validate models on out-of-sample data'
  }];
  const formatNumber = (num: number): string => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  const handleExploreStrategies = (regime: WeatherRegime) => {
    setSelectedRegime(regime);
    setTimeout(() => {
      document.getElementById('strategies-section')?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 100);
  };
  const handleSimulateStrategy = (strategy: StrategyArchetype) => {
    setSelectedStrategy(strategy);
    setShowSimulationModal(true);
    setNotional(1000000);
    setPositionType(strategy.name.includes('Short') ? 'short' : strategy.name.includes('Hedge') ? 'hedge' : 'long');
    setPreferredProducts(strategy.instruments);
    setViewingExistingTrade(null);
    setIsEditingNotionalInModal(false);
  };
  const handleAddTrade = () => {
    if (!selectedStrategy || !selectedRegime) return;
    const existingTradeIndex = simulatedTrades.findIndex(t => t.strategyName === selectedStrategy.name && t.region === selectedRegime.region);
    if (existingTradeIndex !== -1) {
      setSimulatedTrades(trades => trades.filter((_, index) => index !== existingTradeIndex));
      setShowSimulationModal(false);
      setSuccessMessage('Trade removed from plan');
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
      return;
    }
    const newTrade: SimulatedTrade = {
      id: `trade-${Date.now()}`,
      strategyName: selectedStrategy.name,
      region: selectedRegime.region,
      instrument: selectedStrategy.instruments[0],
      positionType,
      notional,
      entryTiming,
      weatherDependency: selectedRegime.pattern,
      timeHorizon: selectedStrategy.holdingPeriod,
      pnlRange: {
        p10: -notional * 0.12,
        p50: notional * 0.08,
        p90: notional * 0.22
      },
      alertsEnabled: true,
      status: 'active',
      lastUpdate: 'Just now'
    };
    setSimulatedTrades([newTrade, ...simulatedTrades]);
    setShowSimulationModal(false);
    setSuccessMessage('Trade added to plan');
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };
  const handleDeleteTrade = (tradeId: string) => {
    setSimulatedTrades(trades => trades.filter(t => t.id !== tradeId));
  };
  const mockStrategies = selectedRegime ? strategyLibrary[selectedRegime.id] || [] : [];
  return <div className="min-h-screen font-sans text-slate-900 py-4 px-4 md:px-6 lg:px-8 overflow-y-auto" data-id="element-3319">
      <div className="max-w-7xl mx-auto space-y-8 pb-20" data-id="element-3320">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-3321">
          <div data-id="element-3322">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-3323">
              Trading Strategies
            </h1>
            <p className="text-slate-500 mt-1 text-sm md:text-base" data-id="element-3324">
              AI-generated trading opportunities based on weather regimes
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-3325">
            <button className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors shadow-sm" data-id="element-3326">
              <Filter size={16} data-id="element-3327" />
              <span data-id="element-3328">Filter</span>
            </button>
            <button className="flex items-center gap-2 px-3 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors shadow-sm" data-id="element-3329">
              <RefreshCw size={16} data-id="element-3330" />
              <span data-id="element-3331">Refresh Analysis</span>
            </button>
          </div>
        </div>

        <section data-id="element-3332">
          <div className="flex items-center justify-between mb-4" data-id="element-3333">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-3334">
              <Wind className="text-slate-400" size={20} data-id="element-3335" />
              Identified Weather Regimes
            </h2>
            <span className="text-xs font-medium px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-100" data-id="element-3336">
              Live Updates Active
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3337">
            {mockRegimes.map(regime => <motion.div key={regime.id} layoutId={regime.id} className={`group relative bg-white rounded-xl border transition-all duration-300 hover:shadow-lg overflow-hidden flex flex-col ${selectedRegime?.id === regime.id ? 'border-blue-500 ring-1 ring-blue-500 shadow-md' : 'border-slate-200 hover:border-blue-300'}`} data-source={`${regime.region} Regime`} data-id="element-3338">
                <div className="p-5 border-b border-slate-100 bg-slate-50/30" data-id="element-3339">
                  <div className="flex justify-between items-start mb-3" data-id="element-3340">
                    <div className="flex items-center gap-2" data-id="element-3341">
                      <span className="text-xs font-bold tracking-wider text-slate-500 uppercase" data-id="element-3342">
                        {regime.region}
                      </span>
                      <span className="text-slate-300" data-id="element-3343">•</span>
                      <span className="text-xs text-slate-500" data-id="element-3344">
                        {regime.timeHorizon}
                      </span>
                    </div>
                    <ConfidenceBadge confidence={regime.confidence} data-id="element-3345" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 leading-tight mb-2 group-hover:text-blue-600 transition-colors" data-id="element-3346">
                    {regime.pattern}
                  </h3>
                  <div className="flex gap-2" data-id="element-3347">
                    <ImpactBadge impact={regime.marketImpact} data-id="element-3348" />
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1" data-id="element-3349">
                  <p className="text-sm text-slate-600 leading-relaxed mb-4" data-id="element-3350">
                    {regime.description}
                  </p>
                  <div className="space-y-2 mb-5 flex-1" data-id="element-3351">
                    {regime.detectionLogic.map((logic, idx) => <div key={idx} className="flex items-start gap-2 text-xs text-slate-500" data-id="element-3352">
                        <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-3353" />
                        <span data-id="element-3354">{logic}</span>
                      </div>)}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-auto" data-id="element-3355">
                    <div className="flex items-center gap-1.5 text-xs text-slate-400" data-id="element-3356">
                      <Clock size={14} data-id="element-3357" />
                      <span data-id="element-3358">Updated {regime.lastUpdated}</span>
                    </div>
                    <button onClick={() => handleExploreStrategies(regime)} className="flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors" data-id="element-3359">
                      Explore Strategies
                      <ArrowRight size={16} data-id="element-3360" />
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </section>

        {simulatedTrades.length > 0 && <section className="bg-white/60 backdrop-blur-xl rounded-xl border border-slate-50 overflow-hidden shadow-sm" data-id="element-3361">
            <div className="p-4 md:p-6 border-b border-slate-100/60 flex items-center justify-between gap-3" data-id="element-3362">
              <div data-id="element-3363">
                <h2 className="text-base md:text-lg font-bold text-slate-900" data-id="element-3364">
                  Active Trade Plan
                </h2>
                <p className="text-xs md:text-sm text-slate-500 mt-0.5" data-id="element-3365">
                  Tracked strategies for off-platform execution
                </p>
              </div>
              <button className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1 md:py-1.5 border border-slate-200 rounded-lg text-[10px] md:text-xs font-medium text-slate-600 hover:bg-slate-50" data-id="element-3366">
                <Download size={12} data-id="element-3367" />
                <span className="hidden sm:inline" data-id="element-3368">Export Plan</span>
              </button>
            </div>
            <div className="overflow-x-auto" data-id="element-3369">
              <table className="w-full text-left border-collapse" data-id="element-3370">
                <thead data-id="element-3371">
                  <tr className="border-b border-slate-100 bg-slate-50/50 text-xs uppercase tracking-wider text-slate-500" data-id="element-3372">
                    <th className="px-4 md:px-6 py-3 font-semibold" data-id="element-3373">
                      Strategy
                    </th>
                    <th className="px-4 md:px-6 py-3 font-semibold" data-id="element-3374">
                      Region & Instrument
                    </th>
                    <th className="px-4 md:px-6 py-3 font-semibold text-right" data-id="element-3375">
                      Notional
                    </th>
                    <th className="px-4 md:px-6 py-3 font-semibold text-right" data-id="element-3376">
                      PnL Range
                    </th>
                    <th className="px-4 md:px-6 py-3 font-semibold text-center" data-id="element-3377">
                      Status
                    </th>
                    <th className="px-4 md:px-6 py-3 font-semibold text-right" data-id="element-3378">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm" data-id="element-3379">
                  {simulatedTrades.map(trade => <tr key={trade.id} className="group hover:bg-slate-50/80 transition-colors" data-id="element-3380">
                      <td className="px-4 md:px-6 py-4" data-id="element-3381">
                        <div className="font-semibold text-slate-900" data-id="element-3382">
                          {trade.strategyName}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5" data-id="element-3383">
                          <span className={`w-1.5 h-1.5 rounded-full ${trade.positionType === 'long' ? 'bg-emerald-500' : trade.positionType === 'short' ? 'bg-rose-500' : 'bg-amber-500'}`} data-id="element-3384" />
                          <span className="capitalize" data-id="element-3385">
                            {trade.positionType}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4" data-id="element-3386">
                        <div className="text-slate-900" data-id="element-3387">{trade.region}</div>
                        <div className="text-xs text-slate-500 font-mono mt-0.5" data-id="element-3388">
                          {trade.instrument}
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-right font-mono" data-id="element-3389">
                        <span className="text-slate-700" data-id="element-3390">
                          €{formatNumber(trade.notional)}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-right" data-id="element-3391">
                        <div className="flex items-center justify-end gap-2 text-xs" data-id="element-3392">
                          <span className="text-red-600 font-medium" data-id="element-3393">
                            {(trade.pnlRange.p10 / 1000).toFixed(0)}k
                          </span>
                          <span className="text-slate-300" data-id="element-3394">|</span>
                          <span className="text-green-600 font-medium" data-id="element-3395">
                            +{(trade.pnlRange.p90 / 1000).toFixed(0)}k
                          </span>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-center" data-id="element-3396">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700" data-id="element-3397">
                          Active
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-right" data-id="element-3398">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3399">
                          <button onClick={() => handleDeleteTrade(trade.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Remove" data-id="element-3400">
                            <Trash2 size={14} data-id="element-3401" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </section>}

        <div id="strategies-section" className="scroll-mt-8" data-id="element-3402">
          {selectedRegime ? <div className="space-y-6" data-id="element-3403">
              <div className="flex items-center justify-between" data-id="element-3404">
                <div data-id="element-3405">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-3406">
                    <TrendingUp className="text-blue-500" size={24} data-id="element-3407" />
                    Available Strategies
                  </h2>
                  <p className="text-slate-500 text-sm mt-1" data-id="element-3408">
                    Tailored for {selectedRegime.region} •{' '}
                    {selectedRegime.pattern}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3409">
                {mockStrategies.map(strategy => {
              const isTradeActive = simulatedTrades.some(t => t.strategyName === strategy.name && t.region === selectedRegime.region);
              return <motion.div key={strategy.id} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} className={`bg-white rounded-xl border p-5 transition-all hover:shadow-md ${isTradeActive ? 'border-blue-200 ring-1 ring-blue-100' : 'border-slate-200 hover:border-blue-300'}`} data-source={`Strategy: ${strategy.name}`} data-id="element-3410">
                      <div className="flex justify-between items-start mb-4" data-id="element-3411">
                        <div data-id="element-3412">
                          <div className="flex items-center gap-2 mb-1" data-id="element-3413">
                            <h3 className="font-bold text-slate-900 text-lg" data-id="element-3414">
                              {strategy.name}
                            </h3>
                            {isTradeActive && <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded-full uppercase tracking-wide" data-id="element-3415">
                                <Check size={10} strokeWidth={3} data-id="element-3416" />
                                Active
                              </span>}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-3417">
                            <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded" data-id="element-3418">
                              {strategy.instruments[0]}
                            </span>
                            <span data-id="element-3419">•</span>
                            <span data-id="element-3420">{strategy.holdingPeriod}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5" data-id="element-3421">
                          <FitBadge fit={strategy.fitScore} data-id="element-3422" />
                          <RiskBadge risk={strategy.riskProfile} data-id="element-3423" />
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mb-4 leading-relaxed" data-id="element-3424">
                        {strategy.description}
                      </p>
                      <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-3425">
                        <div className="bg-emerald-50/50 rounded-lg p-2.5 border border-emerald-100/50" data-id="element-3426">
                          <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide mb-1" data-id="element-3427">
                            Works When
                          </div>
                          <p className="text-xs text-emerald-900 leading-snug" data-id="element-3428">
                            {strategy.worksWhen}
                          </p>
                        </div>
                        <div className="bg-rose-50/50 rounded-lg p-2.5 border border-rose-100/50" data-id="element-3429">
                          <div className="text-[10px] font-bold text-rose-700 uppercase tracking-wide mb-1" data-id="element-3430">
                            Fails When
                          </div>
                          <p className="text-xs text-rose-900 leading-snug" data-id="element-3431">
                            {strategy.failsWhen}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => handleSimulateStrategy(strategy)} className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${isTradeActive ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-slate-900 text-white hover:bg-slate-800 hover:shadow-lg'}`} data-id="element-3432">
                        {isTradeActive ? <>
                            <Edit2 size={16} data-id="element-3433" />
                            Manage Trade
                          </> : <>
                            <TrendingUp size={16} data-id="element-3434" />
                            Simulate Strategy
                          </>}
                      </button>
                    </motion.div>;
            })}
              </div>
            </div> : <div className="bg-slate-50/50 rounded-xl border border-dashed border-slate-300 p-12 text-center" data-id="element-3435">
              <div className="w-16 h-16 bg-white rounded-full shadow-sm flex items-center justify-center mx-auto mb-4" data-id="element-3436">
                <Wind className="text-slate-400" size={32} data-id="element-3437" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-3438">
                Select a Weather Regime
              </h3>
              <p className="text-slate-500 max-w-md mx-auto" data-id="element-3439">
                Choose a regime from the cards above to view tailored trading
                strategies.
              </p>
            </div>}
        </div>
      </div>
    </div>;
}